import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, ArrowUp, ArrowDown, Image as ImageIcon, Youtube, Upload, X, Eye, EyeOff, CheckCircle2, Loader2, Megaphone } from "lucide-react";
import { api, fileUrl, errorMessage, uploadImage, youtubeId, youtubeEmbedUrl, youtubeThumb } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const errMsg = (e) => errorMessage(e);



/* ============================================================ */
export default function AdminHomeContent() {
  const { user } = useAuth();
  const [tab, setTab] = React.useState("banners");
  if (user?.role !== "super_admin") {
    return <div className="bg-white border p-8 text-sm text-neutral-600">Only the Super Admin can manage home page banners and videos.</div>;
  }
  return (
    <div>
      <h1 className="h2-editorial mb-2">Home Page</h1>
      <p className="text-sm text-neutral-500 mb-6">Manage the scrolling announcement bar, sliding banners, the Craftsmanship video and the Reels section. "Shop by Categories" uses the categories ticked "Show in Shop menu" (Admin → Categories).</p>
      <div className="flex gap-1 border-b mb-6 overflow-x-auto no-scrollbar">
        {[["announcement", "Announcement Bar"], ["banners", "Sliding Banners"], ["craft", "Craftsmanship Video"], ["reels", "Reels"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`home-tab-${k}`}
                  className={`px-5 py-3 text-xs uppercase tracking-widest font-semibold -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-[#0E4D34] text-[#0E4D34]" : "border-transparent text-neutral-500 hover:text-neutral-800"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "announcement" && <AnnouncementManager/>}
      {tab === "banners" && <BannersManager/>}
      {tab === "craft" && <CraftManager/>}
      {tab === "reels" && <ReelsManager/>}
    </div>
  );
}

/* ============================================================
   BANNERS
   ============================================================ */
const EMPTY_BANNER = {
  media_type: "image", image_url: "", youtube_url: "", overline: "", title: "", subtitle: "",
  cta_text: "", cta_link: "", cta2_text: "", cta2_link: "", duration: 6, active: true,
};

function BannersManager() {
  const qc = useQueryClient();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-banners"], queryFn: () => api.get("/admin/home/banners").then(r => r.data) });
  const banners = data?.banners || [];
  const max = data?.max || 3;
  const [editing, setEditing] = React.useState(null); // null | "new" | banner object

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["home-content"] }); };

  const remove = async (b) => {
    if (!window.confirm(`Delete banner "${b.title || "Untitled"}"?`)) return;
    try { await api.delete(`/admin/home/banners/${b.id}`); toast.success("Banner deleted"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const ids = banners.map(b => b.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/home/banners/reorder", { ids });
    refresh();
  };
  const toggle = async (b) => {
    try { await api.patch(`/admin/home/banners/${b.id}`, { ...EMPTY_BANNER, ...b, active: !b.active }); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (editing) {
    return <BannerEditor initial={editing === "new" ? EMPTY_BANNER : editing}
                         onCancel={() => setEditing(null)}
                         onSaved={() => { setEditing(null); refresh(); }}/>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="text-sm text-neutral-600">
          <span className="font-semibold text-neutral-900">{banners.length} / {max}</span> banners · slides change automatically on the home page
        </div>
        <button onClick={() => setEditing("new")} disabled={banners.length >= max} className="btn-silk disabled:opacity-40 disabled:cursor-not-allowed" data-testid="banner-add">
          <Plus size={12} className="mr-1"/>Add Banner
        </button>
      </div>
      {banners.length >= max && <p className="text-xs text-neutral-500 mb-4">Maximum {max} banners reached — edit or delete one to add another.</p>}

      {isLoading ? <div className="bg-white border p-6 text-sm text-neutral-500">Loading…</div> :
        banners.length === 0 ? (
          <div className="bg-white border p-10 text-center text-sm text-neutral-500">No banners yet. The hero section is hidden on the home page until you add one.</div>
        ) : (
          <div className="space-y-3">
            {banners.map((b, i) => (
              <div key={b.id} className={`bg-white border flex flex-col sm:flex-row gap-4 p-3 ${b.active ? "" : "opacity-60"}`} data-testid={`banner-row-${i}`}>
                <div className="relative w-full sm:w-56 aspect-[16/7] bg-neutral-900 shrink-0 overflow-hidden">
                  {(b.image_url || b.youtube_id) && <img src={fileUrl(b.image_url || youtubeThumb(b.youtube_id))} alt="" className="w-full h-full object-cover"/>}
                  <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-black/70 text-white px-2 py-1 flex items-center gap-1">
                    {b.media_type === "youtube" ? <><Youtube size={10}/>Video</> : <><ImageIcon size={10}/>Image</>}
                  </span>
                  <span className="absolute bottom-2 left-2 text-[10px] bg-white text-neutral-900 px-1.5 font-semibold">#{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  {b.overline && <div className="text-[10px] uppercase tracking-[0.3em] text-[#0E4D34] font-bold">{b.overline}</div>}
                  <div className="font-serif text-xl truncate">{b.title || <span className="text-neutral-400">Untitled</span>}</div>
                  {b.subtitle && <div className="text-xs text-neutral-500 line-clamp-2 mt-1">{b.subtitle}</div>}
                  <div className="text-[11px] text-neutral-400 mt-2">
                    {b.duration}s per slide{b.cta_text ? ` · Button: ${b.cta_text} → ${b.cta_link}` : ""}{!b.active ? " · Hidden" : ""}
                  </div>
                </div>
                <div className="flex sm:flex-col gap-1 items-center justify-end sm:justify-center">
                  <IconBtn title="Move up" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={14}/></IconBtn>
                  <IconBtn title="Move down" onClick={() => move(i, 1)} disabled={i === banners.length - 1}><ArrowDown size={14}/></IconBtn>
                </div>
                <div className="flex sm:flex-col gap-2 justify-end sm:justify-center">
                  <button onClick={() => setEditing(b)} className="text-xs border px-3 py-2 flex items-center gap-1 hover:border-[#0E4D34]" data-testid={`banner-edit-${i}`}><Edit2 size={12}/>Edit</button>
                  <button onClick={() => toggle(b)} className="text-xs border px-3 py-2 flex items-center gap-1 hover:border-[#0E4D34]">{b.active ? <><EyeOff size={12}/>Hide</> : <><Eye size={12}/>Show</>}</button>
                  <button onClick={() => remove(b)} className="text-xs border px-3 py-2 flex items-center gap-1 text-[#991B1B] hover:border-[#991B1B]" data-testid={`banner-delete-${i}`}><Trash2 size={12}/>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function BannerEditor({ initial, onCancel, onSaved }) {
  const [f, setF] = React.useState({ ...EMPTY_BANNER, ...initial });
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const vid = youtubeId(f.youtube_url);
  const isAutoThumb = f.image_url && /i\.ytimg\.com/.test(f.image_url);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file, "banners"); setF((x) => ({ ...x, image_url: url })); toast.success("Image uploaded"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (f.media_type === "image" && (!f.image_url || isAutoThumb)) return toast.error("Please upload a banner image");
    if (f.media_type === "youtube" && !vid) return toast.error("Please paste a valid YouTube link");
    setBusy(true);
    try {
      const payload = { ...f, duration: Number(f.duration) || 6, image_url: f.media_type === "youtube" && isAutoThumb ? "" : f.image_url };
      if (initial.id) await api.patch(`/admin/home/banners/${initial.id}`, payload);
      else await api.post("/admin/home/banners", payload);
      toast.success("Banner saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="grid lg:grid-cols-[1fr_420px] gap-6" data-testid="banner-editor">
      <div className="bg-white border p-6 space-y-5">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">{initial.id ? "Edit Banner" : "New Banner"}</h3>
          <button type="button" onClick={onCancel} className="text-neutral-500 hover:text-neutral-900"><X size={18}/></button>
        </div>

        <div>
          <Label>Banner type</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["image", "Image", ImageIcon], ["youtube", "YouTube Video", Youtube]].map(([k, label, Icon]) => (
              <button type="button" key={k} onClick={() => set("media_type")(k)} data-testid={`banner-type-${k}`}
                      className={`border p-3 text-sm flex items-center justify-center gap-2 ${f.media_type === k ? "border-[#0E4D34] bg-[#E8F3EE] text-[#0E4D34] font-semibold" : "hover:border-neutral-400"}`}>
                <Icon size={16}/>{label}
              </button>
            ))}
          </div>
        </div>

        {f.media_type === "youtube" && (
          <YouTubeField value={f.youtube_url} onChange={set("youtube_url")} testid="banner-youtube"
                        help="The video plays muted and loops as the banner background. Best with landscape (16:9) videos."/>
        )}

        <div>
          <Label>{f.media_type === "image" ? "Banner image *" : "Poster image (optional — shown while the video loads)"}</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="btn-outline-silk cursor-pointer">
              {uploading ? <Loader2 size={12} className="mr-2 animate-spin"/> : <Upload size={12} className="mr-2"/>}
              {f.image_url && !isAutoThumb ? "Replace image" : "Upload image"}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="banner-image-input"/>
            </label>
            {f.image_url && !isAutoThumb && (
              <button type="button" onClick={() => set("image_url")("")} className="text-xs text-[#991B1B]">Remove image</button>
            )}
          </div>
          <p className="text-[11px] text-neutral-400 mt-1.5">Recommended 1920 × 1000 px, JPG/WEBP, under 10 MB.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Small text above heading (optional)" value={f.overline} onChange={set("overline")} placeholder="Winter Bridal 2026"/>
          <Field label="Seconds on screen" type="number" value={f.duration} onChange={set("duration")} min={3} max={120}/>
        </div>
        <div>
          <Label>Heading (optional)</Label>
          <textarea rows={2} value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder={"Woven in\nSilk & Story."} data-testid="banner-title"
                    className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
          <p className="text-[11px] text-neutral-400 mt-1">Press Enter for a line break. Leave every text field and button empty to show only the image/video.</p>
        </div>
        <div>
          <Label>Description (optional)</Label>
          <textarea rows={2} value={f.subtitle || ""} onChange={(e) => set("subtitle")(e.target.value)}
                    className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Button 1 text (optional)" value={f.cta_text} onChange={set("cta_text")} placeholder="Explore Kanjivarams"/>
          <Field label="Button 1 link" value={f.cta_link} onChange={set("cta_link")} placeholder="/shop/sarees"/>
          <Field label="Button 2 text (optional)" value={f.cta2_text} onChange={set("cta2_text")} placeholder="Custom Inquiry"/>
          <Field label="Button 2 link" value={f.cta2_link} onChange={set("cta2_link")} placeholder="/contact"/>
        </div>
        <button type="button" onClick={() => setF((x) => ({ ...x, overline: "", title: "", subtitle: "", cta_text: "", cta_link: "", cta2_text: "", cta2_link: "" }))}
                className="text-xs text-[#991B1B] underline" data-testid="banner-clear-text">Clear all text & buttons (image/video only)</button>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => set("active")(e.target.checked)}/>Show this banner on the home page</label>

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={busy || uploading} className="btn-silk disabled:opacity-50" data-testid="banner-save">
            {busy && <Loader2 size={12} className="mr-2 animate-spin"/>}Save Banner
          </button>
          <button type="button" onClick={onCancel} className="btn-outline-silk">Cancel</button>
        </div>
      </div>

      {/* Live preview */}
      <div className="lg:sticky lg:top-6 h-fit">
        <Label>Preview</Label>
        <div className="relative aspect-[16/10] bg-neutral-900 overflow-hidden border">
          {f.image_url && <img src={fileUrl(f.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70"/>}
          {f.media_type === "youtube" && vid && (
            <iframe key={vid} title="preview" src={youtubeEmbedUrl(vid)} allow="autoplay; encrypted-media"
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-0 pointer-events-none"
                    style={{ height: "100%", width: "auto", aspectRatio: "16 / 9" }}/>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"/>
          <div className="absolute inset-0 p-6 flex flex-col justify-center text-white max-w-[80%]">
            {f.overline && <div className="text-[8px] tracking-[0.4em] uppercase mb-2 border-l-2 border-[#D4AF37] pl-2">{f.overline}</div>}
            {f.title && <div className="font-serif text-2xl leading-tight mb-2 whitespace-pre-line">{f.title}</div>}
            {f.subtitle && <div className="text-[10px] opacity-90 mb-3 line-clamp-2">{f.subtitle}</div>}
            <div className="flex gap-2">
              {f.cta_text && <span className="bg-[#0E4D34] rounded text-[8px] uppercase tracking-widest px-2 py-1.5">{f.cta_text}</span>}
              {f.cta2_text && <span className="border border-white rounded text-[8px] uppercase tracking-widest px-2 py-1.5">{f.cta2_text}</span>}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

/* ============================================================
   ANNOUNCEMENT BAR (scrolling strip above the header)
   ============================================================ */
function AnnouncementManager() {
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["adm-announcement"], queryFn: () => api.get("/admin/home/announcement").then(r => r.data) });
  const [f, setF] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (data) setF({ enabled: data.enabled !== false, items: data.items?.length ? data.items : [""], speed: data.speed || 30 }); }, [data]);
  if (!f) return <div className="bg-white border p-6 text-sm text-neutral-500">Loading…</div>;

  const setItem = (i, v) => setF((x) => ({ ...x, items: x.items.map((t, j) => (j === i ? v : t)) }));
  const removeItem = (i) => setF((x) => ({ ...x, items: x.items.filter((_, j) => j !== i) }));
  const moveItem = (i, d) => setF((x) => {
    const items = [...x.items]; const j = i + d;
    if (j < 0 || j >= items.length) return x;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...x, items };
  });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/home/announcement", { ...f, speed: Number(f.speed) || 30, items: f.items.map((t) => t.trim()).filter(Boolean) });
      toast.success("Announcement bar saved");
      refetch(); qc.invalidateQueries({ queryKey: ["home-content"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const live = f.items.map((t) => t.trim()).filter(Boolean);

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-6">
      <div className="bg-white border p-6 space-y-5">
        <div className="flex items-center gap-2 font-semibold"><Megaphone size={16} className="text-[#0E4D34]"/>Scrolling announcement bar</div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="ann-enabled"/>Show the bar on every page</label>
        <div>
          <Label>Messages (scroll right → left, separated by ✦)</Label>
          <div className="space-y-2">
            {f.items.map((t, i) => (
              <div key={i} className="flex gap-2">
                <input value={t} onChange={(e) => setItem(i, e.target.value)} maxLength={120} data-testid={`ann-item-${i}`}
                       placeholder="e.g. Complimentary Shipping on orders above ₹3000"
                       className="flex-1 border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
                <IconBtn title="Move up" onClick={() => moveItem(i, -1)} disabled={i === 0}><ArrowUp size={14}/></IconBtn>
                <IconBtn title="Move down" onClick={() => moveItem(i, 1)} disabled={i === f.items.length - 1}><ArrowDown size={14}/></IconBtn>
                <IconBtn title="Delete" onClick={() => removeItem(i)}><Trash2 size={14} className="text-[#991B1B]"/></IconBtn>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setF({ ...f, items: [...f.items, ""] })} className="mt-3 text-xs border px-3 py-2 flex items-center gap-1 hover:border-[#0E4D34]" data-testid="ann-add"><Plus size={12}/>Add message</button>
        </div>
        <div className="max-w-xs">
          <Field label="Scroll speed — seconds per loop (lower = faster)" type="number" min={8} max={200} value={f.speed} onChange={(v) => setF({ ...f, speed: v })}/>
        </div>
        <button onClick={save} disabled={busy} className="btn-silk disabled:opacity-50" data-testid="ann-save">{busy && <Loader2 size={12} className="mr-2 animate-spin"/>}Save</button>
      </div>
      <div>
        <Label>Preview</Label>
        <div className="bg-[#0E4D34] text-white text-[10px] tracking-[0.25em] uppercase py-2 overflow-hidden">
          {f.enabled && live.length ? (
            <div className="marquee-track flex w-max" style={{ animationDuration: `${Math.max(8, Number(f.speed) || 30) / 2}s` }}>
              {[0, 1].map((k) => (
                <div key={k} className="flex shrink-0">
                  {[...live, ...live].map((t, i) => <span key={i} className="whitespace-nowrap px-4">{t} <span className="text-[#D4AF37] pl-4">✦</span></span>)}
                </div>
              ))}
            </div>
          ) : <div className="text-center opacity-70">Bar hidden</div>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   REELS (YouTube Shorts on the home page)
   ============================================================ */
function ReelsManager() {
  const qc = useQueryClient();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-reels"], queryFn: () => api.get("/admin/home/reels").then(r => r.data) });
  const reels = data?.reels || [];
  const shown = data?.shown || 4;
  const [url, setUrl] = React.useState("");
  const [caption, setCaption] = React.useState("");
  const [link, setLink] = React.useState("");
  const [editId, setEditId] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [sec, setSec] = React.useState(null);
  React.useEffect(() => { if (data?.section && !sec) setSec(data.section); }, [data, sec]);
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["home-content"] }); };
  const vid = youtubeId(url);

  const save = async (e) => {
    e.preventDefault();
    if (!vid) return toast.error("Please paste a valid YouTube or YouTube Shorts link");
    setBusy(true);
    try {
      const existing = reels.find(r => r.id === editId);
      if (link.trim() && !/^https?:\/\//i.test(link.trim())) { setBusy(false); return toast.error("The reel link must start with https://"); }
      const body = { youtube_url: url.trim(), caption: caption.trim() || null, link: link.trim() || null, active: existing ? existing.active : true };
      if (editId) await api.patch(`/admin/home/reels/${editId}`, body); else await api.post("/admin/home/reels", body);
      toast.success(editId ? "Reel updated" : "Reel added");
      setUrl(""); setCaption(""); setLink(""); setEditId(null); refresh();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!window.confirm("Remove this reel from the home page?")) return;
    try { await api.delete(`/admin/home/reels/${r.id}`); toast.success("Reel removed"); if (editId === r.id) { setEditId(null); setUrl(""); setCaption(""); setLink(""); } refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/admin/home/reels/${r.id}`, { youtube_url: r.youtube_url, caption: r.caption, link: r.link || null, active: !r.active }); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const move = async (i, d) => {
    const ids = reels.map(r => r.id); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/home/reels/reorder", { ids }); refresh();
  };
  const saveSection = async () => {
    try { await api.put("/admin/home/reels-section", sec); toast.success("Reels section saved"); refresh(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const activeIds = reels.filter(r => r.active).slice(0, shown).map(r => r.id);

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        <form onSubmit={save} className={`bg-white border p-6 space-y-4 ${editId ? "border-[#0E4D34]" : ""}`} data-testid="reel-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{editId ? "Edit reel" : "Add a reel"}</h3>
            {editId && <button type="button" onClick={() => { setEditId(null); setUrl(""); setCaption(""); setLink(""); }} className="text-xs text-neutral-500 hover:text-[#991B1B] flex items-center gap-1"><X size={12}/>Cancel edit</button>}
          </div>
          <YouTubeField value={url} onChange={setUrl} testid="reel-url" help="Upload the reel to YouTube as a Short, then paste its link here (youtube.com/shorts/…). It plays muted on a loop; visitors can turn the sound on."/>
          <Field label="Caption (optional)" value={caption} onChange={setCaption} placeholder="e.g. Pure Mysore silk in emerald"/>
          <div>
            <Field label="Link when a visitor clicks the reel (optional)" value={link} onChange={setLink} placeholder="https://www.instagram.com/reel/…" testid="reel-link"/>
            <p className="text-[11px] text-neutral-500 mt-1">Paste the address of this exact reel (e.g. the Instagram reel). Leave empty to open the YouTube Short.</p>
          </div>
          <button type="submit" disabled={busy} className="btn-silk disabled:opacity-50" data-testid="reel-save">{busy ? "Saving…" : editId ? "Save reel" : "Add reel"}</button>
        </form>
        <div>
          <Label>Preview</Label>
          <div className="relative aspect-[9/16] max-w-[220px] rounded-2xl overflow-hidden bg-neutral-900 border">
            {vid ? <iframe key={vid} title="preview" src={youtubeEmbedUrl(vid)} allow="autoplay; encrypted-media" className="absolute inset-0 w-full h-full border-0"/>
                 : <div className="absolute inset-0 flex items-center justify-center text-neutral-500 text-xs px-4 text-center">Paste a YouTube Shorts link to preview</div>}
          </div>
        </div>
      </div>

      <div className="bg-white border">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
          <div className="font-semibold">Reels ({reels.length})</div>
          <div className="text-xs text-neutral-500">The first {shown} visible reels show on the home page.</div>
        </div>
        {isLoading ? <div className="p-6 text-sm text-neutral-500">Loading…</div> :
          reels.length === 0 ? <div className="p-8 text-center text-sm text-neutral-500">No reels yet — the Reels section is hidden on the home page until you add one.</div> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-4">
              {reels.map((r, i) => (
                <div key={r.id} className={`rounded-xl border overflow-hidden ${r.active ? "" : "opacity-50"} ${editId === r.id ? "ring-2 ring-[#0E4D34]" : ""}`} data-testid={`reel-row-${i}`}>
                  <div className="relative aspect-[9/16] bg-neutral-900">
                    <img src={youtubeThumb(r.youtube_id)} alt="" className="w-full h-full object-cover scale-[1.35]"/>
                    <span className="absolute top-2 left-2 text-[10px] bg-white text-neutral-900 px-1.5 font-semibold">#{i + 1}</span>
                    {activeIds.includes(r.id) && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-[#0E4D34] text-white px-1.5 py-0.5">On home</span>}
                    {!r.active && <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest bg-black/70 text-white px-1.5 py-0.5">Hidden</span>}
                  </div>
                  <div className="p-2 space-y-2">
                    {r.caption && <div className="text-xs text-neutral-700 line-clamp-2">{r.caption}</div>}
                    <a href={r.link || `https://www.youtube.com/shorts/${r.youtube_id}`} target="_blank" rel="noreferrer" className="block text-[10px] text-[#0E4D34] underline truncate" title={r.link || "Opens the YouTube Short"}>{r.link ? r.link.replace(/^https?:\/\/(www\.)?/, "") : "Opens YouTube Short"}</a>
                    <div className="flex items-center gap-1 flex-wrap">
                      <IconBtn title="Move left" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={13} className="-rotate-90"/></IconBtn>
                      <IconBtn title="Move right" onClick={() => move(i, 1)} disabled={i === reels.length - 1}><ArrowDown size={13} className="-rotate-90"/></IconBtn>
                      <IconBtn title="Edit" onClick={() => { setEditId(r.id); setUrl(r.youtube_url); setCaption(r.caption || ""); setLink(r.link || ""); window.scrollTo({ top: 0, behavior: "smooth" }); }}><Edit2 size={13}/></IconBtn>
                      <IconBtn title={r.active ? "Hide" : "Show"} onClick={() => toggle(r)}>{r.active ? <EyeOff size={13}/> : <Eye size={13}/>}</IconBtn>
                      <IconBtn title="Delete" onClick={() => remove(r)}><Trash2 size={13} className="text-[#991B1B]"/></IconBtn>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {sec && (
        <div className="bg-white border p-6 space-y-4 max-w-3xl">
          <h3 className="font-semibold">Section text</h3>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sec.enabled !== false} onChange={e => setSec({ ...sec, enabled: e.target.checked })}/>Show the Reels section on the home page</label>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Heading (leave empty to use your Instagram @handle)" value={sec.title} onChange={v => setSec({ ...sec, title: v })} placeholder="@avishkanavya"/>
            <Field label="Button text" value={sec.button_text} onChange={v => setSec({ ...sec, button_text: v })} placeholder="More reels on Instagram"/>
          </div>
          <Field label="Sub-heading" value={sec.subtitle} onChange={v => setSec({ ...sec, subtitle: v })}/>
          <p className="text-[11px] text-neutral-500">The button opens the Instagram link saved in Admin → Settings → Store contact details.</p>
          <button type="button" onClick={saveSection} className="btn-silk" data-testid="reels-section-save">Save section</button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   CRAFTSMANSHIP VIDEOS
   ============================================================ */
const EMPTY_CRAFT = {
  overline: "Craftsmanship", title: "", description: "", youtube_url: "", poster_url: "",
  button_text: "Watch More", button_link: "/journey", active: true,
};

function CraftManager() {
  const qc = useQueryClient();
  const { data, refetch, isLoading } = useQuery({ queryKey: ["adm-craft"], queryFn: () => api.get("/admin/home/craft-videos").then(r => r.data) });
  const videos = data || [];
  const [editing, setEditing] = React.useState(null);
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["home-content"] }); };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? It will be removed from the home page.`)) return;
    try { await api.delete(`/admin/home/craft-videos/${v.id}`); toast.success("Video deleted"); refresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const ids = videos.map(v => v.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/home/craft-videos/reorder", { ids });
    refresh();
  };

  if (editing) {
    return <CraftEditor initial={editing === "new" ? EMPTY_CRAFT : editing}
                        onCancel={() => setEditing(null)}
                        onSaved={() => { setEditing(null); refresh(); }}/>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="text-sm text-neutral-600">YouTube videos play on loop (muted, with a sound button) in the Craftsmanship section.</div>
        <button onClick={() => setEditing("new")} className="btn-silk" data-testid="craft-add"><Plus size={12} className="mr-1"/>Add Video</button>
      </div>
      {isLoading ? <div className="bg-white border p-6 text-sm text-neutral-500">Loading…</div> :
        videos.length === 0 ? (
          <div className="bg-white border p-10 text-center text-sm text-neutral-500">No craftsmanship video. The section is hidden on the home page until you add one.</div>
        ) : (
          <div className="space-y-3">
            {videos.map((v, i) => (
              <div key={v.id} className={`bg-white border flex flex-col sm:flex-row gap-4 p-3 ${v.active ? "" : "opacity-60"}`} data-testid={`craft-row-${i}`}>
                <div className="relative w-full sm:w-56 aspect-video bg-neutral-900 shrink-0 overflow-hidden">
                  {(v.youtube_id || v.poster_url) && <img src={fileUrl(v.youtube_id ? youtubeThumb(v.youtube_id) : v.poster_url)} alt="" className="w-full h-full object-cover"/>}
                  {!v.youtube_id && <span className="absolute inset-x-0 bottom-0 bg-[#991B1B] text-white text-[10px] uppercase tracking-widest text-center py-1">No video link yet</span>}
                </div>
                <div className="flex-1 min-w-0">
                  {v.overline && <div className="text-[10px] uppercase tracking-[0.3em] text-[#0E4D34] font-bold">{v.overline}</div>}
                  <div className="font-serif text-xl">{v.title}</div>
                  {v.description && <div className="text-xs text-neutral-500 line-clamp-2 mt-1">{v.description}</div>}
                  {v.youtube_url && <a href={v.youtube_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#0E4D34] underline mt-2 inline-block break-all">{v.youtube_url}</a>}
                </div>
                {videos.length > 1 && (
                  <div className="flex sm:flex-col gap-1 items-center justify-end sm:justify-center">
                    <IconBtn title="Move up" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={14}/></IconBtn>
                    <IconBtn title="Move down" onClick={() => move(i, 1)} disabled={i === videos.length - 1}><ArrowDown size={14}/></IconBtn>
                  </div>
                )}
                <div className="flex sm:flex-col gap-2 justify-end sm:justify-center">
                  <button onClick={() => setEditing(v)} className="text-xs border px-3 py-2 flex items-center gap-1 hover:border-[#0E4D34]" data-testid={`craft-edit-${i}`}><Edit2 size={12}/>Edit</button>
                  <button onClick={() => remove(v)} className="text-xs border px-3 py-2 flex items-center gap-1 text-[#991B1B] hover:border-[#991B1B]" data-testid={`craft-delete-${i}`}><Trash2 size={12}/>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function CraftEditor({ initial, onCancel, onSaved }) {
  const [f, setF] = React.useState({ ...EMPTY_CRAFT, ...initial, youtube_url: initial.youtube_url || "" });
  const [busy, setBusy] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const vid = youtubeId(f.youtube_url);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file, "home"); setF((x) => ({ ...x, poster_url: url })); toast.success("Poster uploaded"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Title is required");
    if (!vid) return toast.error("Please paste a valid YouTube link");
    setBusy(true);
    try {
      if (initial.id) await api.patch(`/admin/home/craft-videos/${initial.id}`, f);
      else await api.post("/admin/home/craft-videos", f);
      toast.success("Craftsmanship video saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="grid lg:grid-cols-[1fr_440px] gap-6" data-testid="craft-editor">
      <div className="bg-white border p-6 space-y-5">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">{initial.id ? "Edit Craftsmanship Video" : "New Craftsmanship Video"}</h3>
          <button type="button" onClick={onCancel} className="text-neutral-500 hover:text-neutral-900"><X size={18}/></button>
        </div>
        <YouTubeField value={f.youtube_url} onChange={set("youtube_url")} testid="craft-youtube"
                      help="Plays automatically on a continuous loop (muted — visitors can turn sound on)."/>
        <Field label="Small text above heading" value={f.overline} onChange={set("overline")} placeholder="Craftsmanship"/>
        <Field label="Heading *" value={f.title} onChange={set("title")} placeholder="The Art of Weaving Kanjivaram" testid="craft-title"/>
        <div>
          <Label>Description</Label>
          <textarea rows={3} value={f.description || ""} onChange={(e) => set("description")(e.target.value)}
                    className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Button text (optional)" value={f.button_text} onChange={set("button_text")} placeholder="Watch More"/>
          <Field label="Button link" value={f.button_link} onChange={set("button_link")} placeholder="/journey or https://youtube.com/@channel"/>
        </div>
        <div>
          <Label>Poster image (optional — shown while the video loads)</Label>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="btn-outline-silk cursor-pointer">
              {uploading ? <Loader2 size={12} className="mr-2 animate-spin"/> : <Upload size={12} className="mr-2"/>}
              {f.poster_url ? "Replace poster" : "Upload poster"}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden"/>
            </label>
            {f.poster_url && (
              <>
                <img src={fileUrl(f.poster_url)} alt="" className="h-12 aspect-video object-cover border"/>
                <button type="button" onClick={() => set("poster_url")("")} className="text-xs text-[#991B1B]">Remove</button>
              </>
            )}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => set("active")(e.target.checked)}/>Show on the home page</label>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={busy || uploading} className="btn-silk disabled:opacity-50" data-testid="craft-save">
            {busy && <Loader2 size={12} className="mr-2 animate-spin"/>}Save Video
          </button>
          <button type="button" onClick={onCancel} className="btn-outline-silk">Cancel</button>
        </div>
      </div>

      <div className="lg:sticky lg:top-6 h-fit">
        <Label>Preview (loops like on the website)</Label>
        <div className="relative aspect-video bg-neutral-900 overflow-hidden border">
          {vid ? (
            <iframe key={vid} title="preview" src={youtubeEmbedUrl(vid)} allow="autoplay; encrypted-media" className="absolute inset-0 w-full h-full border-0"/>
          ) : f.poster_url ? (
            <img src={fileUrl(f.poster_url)} alt="" className="absolute inset-0 w-full h-full object-cover"/>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-neutral-500 text-xs">Paste a YouTube link to preview</div>
          )}
        </div>
      </div>
    </form>
  );
}

/* ============================================================
   shared bits
   ============================================================ */
function YouTubeField({ value, onChange, help, testid }) {
  const [status, setStatus] = React.useState(null); // null | "checking" | {ok} | {error}
  const vid = youtubeId(value);
  React.useEffect(() => { setStatus(null); }, [value]);

  const check = async () => {
    if (!vid) return setStatus({ error: "That doesn't look like a YouTube link." });
    setStatus("checking");
    try { await api.get("/youtube/check", { params: { url: value } }); setStatus({ ok: true }); }
    catch (e) { setStatus({ error: errMsg(e) }); }
  };

  return (
    <div>
      <Label>YouTube video link *</Label>
      <div className="flex gap-2">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
               placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
               className="flex-1 border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
        <button type="button" onClick={check} className="btn-outline-silk whitespace-nowrap">
          {status === "checking" ? <Loader2 size={12} className="animate-spin"/> : "Check link"}
        </button>
      </div>
      {value && !vid && <p className="text-[11px] text-[#991B1B] mt-1.5">Not a valid YouTube link.</p>}
      {status?.ok && <p className="text-[11px] text-[#0E4D34] mt-1.5 flex items-center gap-1"><CheckCircle2 size={12}/>Video found and can be embedded.</p>}
      {status?.error && <p className="text-[11px] text-[#991B1B] mt-1.5">{status.error}</p>}
      {help && <p className="text-[11px] text-neutral-400 mt-1.5">{help}</p>}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-xs uppercase tracking-widest text-neutral-600 block mb-1.5">{children}</label>;
}
function Field({ label, value, onChange, type = "text", placeholder, testid, min, max }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type={type} value={value ?? ""} min={min} max={max} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full border border-neutral-300 p-2.5 text-sm focus:outline-none focus:border-[#0E4D34]"/>
    </div>
  );
}
function IconBtn({ children, ...rest }) {
  return <button type="button" {...rest} className="p-1.5 border hover:border-[#0E4D34] disabled:opacity-30 disabled:cursor-not-allowed">{children}</button>;
}
